/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { AccountingTable10Info } from "./info";
import { AccountingTable10Service } from "services/accounting-table-10";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  month: (today.getMonth() + 1).toString(),
  year: today.getFullYear().toString(),
  debitCounterpartLabels: ["511", "711", "331", "...", "..."],
  creditCounterpartLabels: ["111", "112", "521", "...", "....."],
  rows: [
    {
      index: 1,
      customerName: "",
      openingDebitBalance: 0,
      debitSideValues: [0, 0, 0, 0, 0],
      debitSideTotal: 0,
      creditSideValues: [0, 0, 0, 0, 0],
      creditSideTotal: 0,
      closingDebitBalance: 0,
    },
  ],
  totals: {
    openingDebitBalance: 0,
    debitSideValues: [0, 0, 0, 0, 0],
    debitSideTotal: 0,
    creditSideValues: [0, 0, 0, 0, 0],
    creditSideTotal: 0,
    closingDebitBalance: 0,
  },
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  // UI helpers
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function AccountingTable10Detail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const response = await AccountingTable10Service.getById(id);
          const apiData = response.data || response;

          // Parse reportDate
          let sDay = initialValues.signingDay;
          let sMonth = initialValues.signingMonth;
          let sYear = initialValues.signingYear;
          if (apiData.reportDate && apiData.reportDate.includes("/")) {
            const parts = apiData.reportDate.split("/");
            if (parts.length === 3) {
              [sDay, sMonth, sYear] = parts;
            }
          }

          setFormInitials({
            ...apiData,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } catch (error) {
          console.error("Lỗi load Bảng kê số 10:", error);
          toast.error("Không thể tải dữ liệu");
        }
      })();
    }
  }, [id]);

  const handleBack = () => navigation("/templates/accounting-table-10");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        ...fValues,
        rows: (fValues.rows || []).map((r) => ({
          ...r,
          index: num(r.index),
          openingDebitBalance: num(r.openingDebitBalance),
          debitSideValues: (r.debitSideValues || []).map(num),
          debitSideTotal: num(r.debitSideTotal),
          creditSideValues: (r.creditSideValues || []).map(num),
          creditSideTotal: num(r.creditSideTotal),
          closingDebitBalance: num(r.closingDebitBalance),
        })),
        totals: {
          openingDebitBalance: num(fValues.totals.openingDebitBalance),
          debitSideValues: (fValues.totals.debitSideValues || []).map(num),
          debitSideTotal: num(fValues.totals.debitSideTotal),
          creditSideValues: (fValues.totals.creditSideValues || []).map(num),
          creditSideTotal: num(fValues.totals.creditSideTotal),
          closingDebitBalance: num(fValues.totals.closingDebitBalance),
        },
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await AccountingTable10Service.create(payload);
        toast.success("Tạo thành công!");
        navigation("/templates/accounting-table-10");
      } else {
        await AccountingTable10Service.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>In Bảng kê số 10 S04b11-DN</title>
          <style>@media print { @page { size: A4 landscape; margin: 1cm; } }</style>
        </head>
        <body onload="setTimeout(() => [window.print(), window.close()], 500)">${html}</body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <AccountingTable10Info formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>
                          Trở về
                        </SoftButton>
                        <SoftBox display="flex" gap={2}>
                          {containView ? (
                            <SoftButton
                              variant="gradient"
                              color="success"
                              onClick={() => handlePrint(values)}
                            >
                              In Bảng kê S04b11-DN
                            </SoftButton>
                          ) : (
                            <SoftButton
                              disabled={isSubmitting}
                              type="submit"
                              variant="gradient"
                              color="dark"
                            >
                              {id ? "Lưu" : "Tạo mới"}
                            </SoftButton>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AccountingTable10Detail;
